// src/features/customer-space/CustomerLoyaltyContext.tsx
import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from "react";

export type LoyaltyLevelPublic = {
    id: number;
    quantity: number;
    color?: string | null;
    name?: string | null;
    description?: string | null;
    image_url?: string | null;
};

export type CustomerLoyaltyState = {
    earningName: string;
    loyalty_points: number;
    loyalty_level_points: number;
    levels: LoyaltyLevelPublic[];
};

/** ---- Notifications ---- */
export type NotificationType = "alert" | "erreur" | "succes";
export type NotificationPayload = {
    type: NotificationType;
    message: string;
    /** Titre personnalisé (optionnel) */
    title?: string;
    /** durée en ms, défaut 3000 */
    duration?: number;
};
type NotificationState =
    | (NotificationPayload & { id: number; expiresAt?: number })
    | null;

type CustomerLoyaltyContextType = CustomerLoyaltyState & {
    setLoyalty: React.Dispatch<React.SetStateAction<CustomerLoyaltyState>>;
    /** Déclenche une notification dans le layout */
    setNotification: (n: NotificationPayload) => void;
    /** Permet de lire/fermer depuis le layout */
    notification: NotificationState;
    clearNotification: () => void;
};

const CustomerLoyaltyContext = createContext<CustomerLoyaltyContextType | undefined>(undefined);

export function CustomerLoyaltyProvider({
                                            children,
                                            initial,
                                        }: {
    children: React.ReactNode;
    initial?: Partial<CustomerLoyaltyState>;
}) {
    const [state, setState] = useState<CustomerLoyaltyState>({
        earningName: initial?.earningName ?? "Points",
        loyalty_points: initial?.loyalty_points ?? 1147,
        loyalty_level_points: initial?.loyalty_level_points ?? 212,
        levels: initial?.levels ?? [],
    });

    // ---- Notifications ----
    const [notification, setNotificationState] = useState<NotificationState>(null);
    const timerRef = useRef<number | null>(null);

    const clearNotification = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setNotificationState(null);
    };

    const setNotification = (n: NotificationPayload) => {
        // Reset ancien timer s’il existe
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const duration = typeof n.duration === "number" ? n.duration : 3000;
        const next = {
            ...n,
            id: Date.now(),
            expiresAt: duration > 0 ? Date.now() + duration : undefined,
        } as NotificationState;

        setNotificationState(next);

        if (duration > 0) {
            timerRef.current = window.setTimeout(() => {
                timerRef.current = null;
                setNotificationState((cur) => (cur?.id === next?.id ? null : cur));
            }, duration) as unknown as number;
        }
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!initial) return;
        setState((prev) => {
            const next: CustomerLoyaltyState = {
                earningName: initial.earningName ?? prev.earningName,
                loyalty_points: initial.loyalty_points ?? prev.loyalty_points,
                loyalty_level_points: initial.loyalty_level_points ?? prev.loyalty_level_points,
                levels: initial.levels ?? prev.levels,
            };
            const changed =
                next.earningName !== prev.earningName ||
                next.loyalty_points !== prev.loyalty_points ||
                next.loyalty_level_points !== prev.loyalty_level_points ||
                next.levels !== prev.levels;
            return changed ? next : prev;
        });
    }, [initial?.earningName, initial?.loyalty_points, initial?.loyalty_level_points, initial?.levels]);

    const value = useMemo(
        () => ({
            ...state,
            setLoyalty: setState,
            // notifications
            setNotification,
            notification,
            clearNotification,
        }),
        [state, notification]
    );

    return <CustomerLoyaltyContext.Provider value={value}>{children}</CustomerLoyaltyContext.Provider>;
}

export function useCustomerLoyalty() {
    const ctx = useContext(CustomerLoyaltyContext);
    if (!ctx) throw new Error("useCustomerLoyalty must be used within CustomerLoyaltyProvider");
    return ctx;
}
